import { rnd2 } from "../../../Game";
import { ArrayUtils } from "../../../Utils/Collections/Array";
import { NumValBase } from "../../../Utils/NumberValue";
import { TIMER } from "../../Gameplay";
import { RoundManager } from "../../Round/RoundManager";
import { Stadium } from "../../Stadium/Stadium";
import { RoundTarget } from "../Targets/RoundTarget";
import { eTargetState, TargetBase } from "../Targets/Target";
import { TargetSettingsBase } from "../TargetSettings";
import { eTargetType } from "../TargetType";
import { TargetControllerBase } from "./BaseController";



export const enum eSwitchPosTargetMode {
    /**
     * Pozice se vybira nahodne
     */
    random,
    /**
     * Postupně od první po poslední
     */
    inOrderFromZero,
    /**
     * Postupně od náhodného indexu
     */
    inOrderFromRnd,
}



export class SwitchPosTargetSettings extends TargetSettingsBase {
    get positions(): pc.Vec2[] | NumValBase[] { return this._positions; }
    private _positions: pc.Vec2[] | NumValBase[];
    /**
     * (RO) Rychlost prepinani pozic v ms pro nejnizsi obtiznost
     */
    get minDifSwitchInterval(): number { return this._minDifSwitchInterval; }
    private _minDifSwitchInterval: number;
    /**
     * (RO) Rychlost prepinani pozic v ms pro nejvyssi obtiznost
     */
    get maxDifSwitchInterval(): number { return this._maxDifSwitchInterval; }
    private _maxDifSwitchInterval: number;

    get switchMode(): eSwitchPosTargetMode { return this._switchMode; }
    private _switchMode: eSwitchPosTargetMode;

    //-------------------------------------------------------------------------
    /**
     * 
     * @param type 
     * @param scale 
     * @param positions Pozice, na kterych se ma terc zobrazovat. Pokud se jedna o pole NumValBase, tak je to [x1, y1, x2, y2, ..., xn, yn]
     * @param minDifSwitchInterval Rychlost prepinani pozic v ms pro nejnizsi obtiznost
     * @param maxDifSwitchInterval Rychlost prepinani pozic v ms pro nejvyssi obtiznost
     * @param switchMode Rezim prepinani
     */
    //-------------------------------------------------------------------------
    constructor(type: eTargetType, scale: number, positions: pc.Vec2[] | NumValBase[], minDifSwitchInterval: number, maxDifSwitchInterval: number, switchMode: eSwitchPosTargetMode, flags?: number) {
        super(type, scale, flags);

        this._positions = positions;
        this._minDifSwitchInterval = minDifSwitchInterval;
        this._maxDifSwitchInterval = maxDifSwitchInterval;
        this._switchMode = switchMode;
    }
}



const enum eControllerState {
    showTarget,
    idle,
    hideTarget,
}



export class SwitchPosTargetController extends TargetControllerBase {
    private static readonly HID_ANIM_LEN = 500;

    get positions(): pc.Vec2[] { return this._positions; }
    private _positions: pc.Vec2[] = [];

    private _nextPosId: number;
    private _switchInterval: number;
    private _state: eControllerState;
    private static _stateProcessFncs: ((time: number) => void)[];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        super();

        if (!SwitchPosTargetController._stateProcessFncs) {
            SwitchPosTargetController._stateProcessFncs = [];
            SwitchPosTargetController._stateProcessFncs[eControllerState.showTarget] = this.processShowTarget;
            SwitchPosTargetController._stateProcessFncs[eControllerState.idle] = this.processIdle;
            SwitchPosTargetController._stateProcessFncs[eControllerState.hideTarget] = this.processHideTarget;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(target: TargetBase, settings: SwitchPosTargetSettings): void {
        super.init(target, settings);

        let roundMng = RoundManager.instance;

        // Interval prepinani pozic
        let interval = settings.minDifSwitchInterval + (settings.maxDifSwitchInterval - settings.minDifSwitchInterval) * roundMng.roundDifficultyIncRatio;

        /*if (rndMng.roundAttemptId > 0)
            interval += interval * (0.15 * Math.min(4, rndMng.roundAttemptId));*/

        this._switchInterval = interval;

        // Seznam pozici
        this.fillPosList();

        // Nastavit prvni pozici
        this.setNextPosition();
        this._state = eControllerState.idle;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    restart(): void {
        this._nextPosId = 0;
        this.setNextPosition();
        this._state = eControllerState.idle;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._target.state == eTargetState.idle) {
            let state: eControllerState;

            //if (Ball.instance.state <= eBallState.waitForKickOff) {
            do {
                state = this._state;
                SwitchPosTargetController._stateProcessFncs[state].call(this, (TIMER.time - this._target.stateTime) % this._switchInterval);
            } while (state != this._state);

            (<RoundTarget>this._target).fillRatio = 1 - ((TIMER.time - this._target.stateTime) % this._switchInterval) / (this._switchInterval - SwitchPosTargetController.HID_ANIM_LEN);
            //}
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci pozici terce v zadany cas
     * @param time 
     * @returns 
     */
    //-------------------------------------------------------------------------
    getPositionInTime(time: number): pc.Vec2 {
        // Ziskat index pozice v zadany cas        
        let posId = Math.floor((time - this._target.stateTime) / this._switchInterval) % this._positions.length;
        let worldPos = new pc.Vec2().copy(this._positions[posId]);

        return worldPos;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processShowTarget(time: number): void {
        let progress = time / 500;

        if (progress >= 1) {
            progress = 1;
            this._state = eControllerState.idle;
        }

        this._target.scale = 0.25 + 0.75 * pc.ElasticOut(progress);
        this._target.opacity = pc.CubicOut(progress);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processIdle(time: number): void {
        if (time > this._switchInterval - SwitchPosTargetController.HID_ANIM_LEN)
            this._state = eControllerState.hideTarget;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processHideTarget(time: number): void {
        let progress = (time - (this._switchInterval - SwitchPosTargetController.HID_ANIM_LEN)) / SwitchPosTargetController.HID_ANIM_LEN;

        if (progress > 0) {
            this._target.scale = 1 - 0.75 * pc.CubicIn(progress);
            this._target.opacity = 1 - pc.CubicOut(progress);
        }
        else {
            // Zobrazit terc na nove pozici
            this.setNextPosition();
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private setNextPosition(): void {
        // Nastavit pozici
        this._pos.copy(this._positions[this._nextPosId]);
        this._target.container.setPosition(this._pos.x, this._pos.y, Stadium.END_Z + this._target.id * TargetBase.Z_SPACING);

        // Index pristi pozice
        if (++this._nextPosId == this._positions.length)
            this._nextPosId = 0;

        // Zobrazit terc pomoci animace
        this._state = eControllerState.showTarget;
    }

    //-------------------------------------------------------------------------
    /**
     * Naplni seznam pozici terce a nastavi index pristi pozice
     */
    //-------------------------------------------------------------------------
    private fillPosList(): void {
        let settings = this._settings as SwitchPosTargetSettings;
        let positions = settings.positions;

        this._positions.length = 0;

        if (positions[0] instanceof pc.Vec2) {
            let i2 = settings.switchMode != eSwitchPosTargetMode.inOrderFromRnd ? 0 : rnd2.randomIntIn(0, positions.length - 1);

            for (let i1 = 0; i1 < positions.length; i1++) {
                this._positions[i1] = new pc.Vec2().copy(positions[i2] as pc.Vec2);

                if (++i2 == positions.length)
                    i2 = 0;
            }
        }
        else {
            let i = 0;

            while (i < positions.length) {
                this._positions.push(new pc.Vec2(positions[i] != undefined ? NumValBase.getValue(positions[i] as NumValBase) : this._positions[this._positions.length - 1].x,
                    positions[i + 1] != undefined ? NumValBase.getValue(positions[i + 1] as NumValBase) : this._positions[this._positions.length - 1].y));

                i += 2;
            }
        }

        // Prevest pomerove pozice na pozice v hernim svete
        this._positions.forEach((pos: pc.Vec2): void => { this.convertRatioPos2WorldPos(pos.x, pos.y, pos); });

        this._nextPosId = 0;

        if (settings.switchMode == eSwitchPosTargetMode.random)
            ArrayUtils.shuffle<pc.Vec2>(this._positions, rnd2);
    }
}